<template>
  <div class="RunWorkflow mt-5">
    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <h3>Select Workflow to trigger</h3>
      </v-col>
      <v-col cols="auto">
        <v-autocomplete
          class="mb-3"
          outlined
          dense
          :value="settings.workflow"
          @input="(val) => this.setWorkflow(val)"
          @click:append-outer="listWorkflows"
          item-text="name"
          item-value="id"
          :items="workflowsList"
          label="Workflow"
          :append-outer-icon="loading ? 'mdi-loading mdi-spin' : 'mdi-reload'"
          clearable
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <v-switch
          label="Silent Run"
          v-model="settings.silentRun"
          @change="(val) => onUpdate(val, `settings.silentRun`)"
        >
          Silent Run
        </v-switch>
      </v-col>
    </v-row>

    <v-row dense align="center">
      <v-col cols="auto" class="mr-auto">
        <p class="text-subtitle-1 mb-2">Input Fields</p>
      </v-col>
    </v-row>

    <div
      class="RunWorkflow__param-row mb-6"
      v-for="(param, i) in settings.params"
      :key="i"
    >
      <v-text-field
        class="RunWorkflow__param-row--field mr-2"
        outlined
        disabled
        dense
        label="Name"
        :value="param.name"
        @input="(val) => onUpdate(val, `settings.params[${i}].name`)"
      />
      <v-combobox
        class="RunWorkflow__param-row--field mr-2"
        outlined
        dense
        :value="param.value"
        @input="
          (val) => onUpdate(val?.name || val, `settings.params[${i}].value`)
        "
        item-text="name"
        item-value="name"
        :items="availableInputFields"
        label="Value"
        clearable
      ></v-combobox>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import 'vue-json-viewer/style.css';
import cloneDeep from 'lodash/cloneDeep';
import { getModelType } from '@/util/actionsModels';
import { helpers } from '@/mixins/helpers';

const {
  mapGetters: workflowDetailsGetters,
  mapActions: workflowDetailsActions,
} = createNamespacedHelpers('workflows/details');
const { mapGetters: WorkflowGetters, mapActions: WorkflowActions } =
  createNamespacedHelpers('workflows/list');

export default {
  name: 'run_workflow',
  props: {
    availableInputFields: { required: true },
  },
  mixins: [helpers],
  data() {
    return {
      testingScript: false,
      newInput: undefined,
      testOutput: null,
      showDialog: false,
      selectedWorkflow: {},
      newInputScript: '',
      testScriptOutput: '',
      placeholder: '',
      error: '',
    };
  },
  computed: {
    ...workflowDetailsGetters({
      selectedNode: 'SELECTED_NODE',
      currentWorkflow: 'WORKFLOW',
    }),
    ...WorkflowGetters({
      workflows: 'AUTOMATION_WORKFLOWS_NAMES',
      loading: 'FETCH_AUTOMATION_WORKFLOWS_NAMES_LOADING',
    }),
    settings() {
      return this.selectedNode.settings;
    },
    workflowsList() {
      return [...this.workflows, this.settings.workflow];
    },
  },
  methods: {
    ...WorkflowActions({
      fetchWorkflows: 'fetchWorkflowsNames',
    }),
    ...workflowDetailsActions(['executeSingleAction', 'getWorkflowTriggerIO']),
    onUpdate(value, path) {
      this.$emit('update', value, path);
    },
    onUpdateScript(value) {
      this.newInputScript = value;
    },
    async listWorkflows() {
      await this.fetchWorkflows({
        customer_id: this.currentWorkflow?.customer_id,
      });
    },
    async setWorkflow(workflow_id) {
      const workflow = this.workflows.find(
        (workflow) => workflow.id === workflow_id
      );
      if (workflow) {
        this.onUpdate(workflow, 'settings.workflow');
        await this.onWorkflowSelect(workflow_id);
      }
    },
    addParam() {
      let settings = cloneDeep(this.settings);
      if (!settings.params) {
        settings.params = [];
      }

      settings.params.push({
        name: '',
        value: '',
      });
      this.onUpdate(settings, 'settings');
    },
    deleteParam(index) {
      const settings = cloneDeep(this.settings);
      settings.params.splice(index, 1);
      if (!settings.params.length) delete settings.params;

      this.onUpdate(settings, 'settings');
    },
    showAddParamBtn() {
      const params = this.settings.params;
      return (
        !params ||
        !params[0] ||
        (params[params.length - 1].name && params[params.length - 1].value)
      );
    },
    async testScript() {
      try {
        this.error = '';
        this.output = '';
        this.testingScript = true;
        const params = [];
        if (this.settings.params) {
          this.settings.params.forEach((param) => {
            if (typeof param.value === 'string') {
              params.push({ name: param.name, value: param.value });
            } else {
              params.push({ name: param.name, value: param.value.name });
            }
          });
        }

        const options = {
          data: {
            params: params,
            test_mode: true,
            workflow: this.settings.workflow,
          },
          input: this.newInput || {},
          action: this.selectedNode.action_type || 'run_workflow',
        };
        const data = await this.executeSingleAction(options);
        if (data.success) {
          this.output = data.result || 'no output';
          this.testOutput = data.model;
          const output_type = getModelType(data.model);
          this.onUpdate(output_type, 'output_type');
        } else {
          throw Error(data.message);
        }
      } catch (e) {
        this.error = e.data ? e.data.message : e;
      } finally {
        this.testingScript = false;
      }
    },
    async runTestInput() {
      try {
        this.error = '';
        this.output = '';
        this.testingScript = true;

        const options = {
          creds: null,
          data: {
            script: this.newInputScript,
          },
          action: 'execute_javascript',
          params: [],
        };
        const data = await this.executeSingleAction(options);
        if (!data || !data.success) {
          throw Error(data && data.message);
        }
        const output = data.data ?? data.result ?? 'no output';
        this.testScriptOutput = output;
        this.newInput = output;
      } catch (e) {
        this.error = e.data ? e.data.message : e;
      } finally {
        this.testingScript = false;
      }
    },
    async onWorkflowSelect(id) {
      const data = await this.getWorkflowTriggerIO({ workflow_id: id });
      const settings = cloneDeep(this.settings);
      settings.params = data.input;
      this.onUpdate(settings, 'settings');
      this.onUpdate(data.output, 'output_type');
    },
  },
};
</script>

<style scoped lang="scss">
@import './run-workflow';
</style>
